import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant, tenantIdsEqual } from '@/lib/tenantUtils';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Trash2, Copy } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import {
  runDiyPlaybookGenerationPoll,
  getActiveDiyGenerationPollCount,
  diyGenerationSafetyCapMs,
} from '@/lib/diyGenerationPoll';
import { normalizeRunSequenceIds } from '@/lib/playbookStatusAggregate';
import { cloneDiyPlaybook } from '@/lib/cloneDiyPlaybook';
import DIYPlaybookForm from '../components/playbooks/DIYPlaybookForm';
import {
  buildDiyStepAssetCatalogFromPersonalization,
  sanitizeDiyStepsArray,
} from '@/lib/diyStepCatalogSanitize';
import {
  diyPlaybookLinkedInSubtypeWire,
  enforceLinkedInChannelSequence,
  resolveDiyStepChannelFromApiStep,
} from '@/lib/outboxChannel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

const DIY_GEN_INFLIGHT_PREFIX = 'diy-gen-inflight';

function diyGenInflightLatestKey(tenantId, userId) {
  return `${DIY_GEN_INFLIGHT_PREFIX}:latest:${String(tenantId)}:${String(userId)}`;
}

function readDiyGenInflightLatest(tenantId, userId) {
  try {
    const raw = localStorage.getItem(diyGenInflightLatestKey(tenantId, userId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.expiresAt !== 'number') return null;
    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(diyGenInflightLatestKey(tenantId, userId));
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeDiyGenInflightLatest(tenantId, userId, payload) {
  try {
    localStorage.setItem(diyGenInflightLatestKey(tenantId, userId), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

function clearDiyGenInflightLatest(tenantId, userId) {
  try {
    localStorage.removeItem(diyGenInflightLatestKey(tenantId, userId));
  } catch {
    // ignore
  }
}

const LOCAL_STORAGE_KEY = 'diy-playbooks';

async function fetchPersonalizationCatalogForDiy(tenantId, userId) {
  const tid = String(tenantId || '').trim();
  const uid = String(userId || '').trim();
  if (!tid || !uid) return null;
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/settings/personalization?tenant_id=${encodeURIComponent(tid)}&user_id=${encodeURIComponent(uid)}`,
      { credentials: 'include' }
    );
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// Map API response (snake_case) to form values (camelCase).
// When `personalizationData` is provided, per-step optional keys are stripped if absent from that catalog.
const mapApiToForm = (apiData, personalizationData) => {
  if (!apiData) return null;

  let steps = (apiData.steps || []).map(step => ({
    id: step.id || crypto.randomUUID(),
    order: step.order || step.step_order || 1,
    channel: resolveDiyStepChannelFromApiStep(step),
    dayOffset: step.day_offset ?? step.dayOffset ?? 0,
    strategicHook: step.strategic_hook || step.strategicHook || '',
    messageFocus: step.message_focus || step.messageFocus || '',
    kpiKey: step.kpi_key || step.kpiKey || null,
    hookKey: step.hook_key || step.hookKey || null,
    caseStudyKey: step.case_study_key || step.caseStudyKey || null,
    productLinkKeys: step.product_link_keys || step.productLinkKeys || [],
    educationalResourceKeys:
      step.educationalResourceKeys ||
      step.educational_resource_keys ||
      step.signals_used?.educational_resource_keys ||
      step.signals_used?.educationalResourceKeys ||
      [],
    ctaStyleKey: step.cta_style_key || step.ctaStyleKey || null,
  }));

  steps = enforceLinkedInChannelSequence(steps);

  if (personalizationData) {
    const catalog = buildDiyStepAssetCatalogFromPersonalization(personalizationData);
    if (catalog) {
      steps = sanitizeDiyStepsArray(steps, catalog, { logWarnings: true }).steps;
    }
  }

  return {
    // Basic info
    name: apiData.name || '',
    goalDescription: apiData.goal_description || apiData.goalDescription || '',

    // Country
    countryMode:
      apiData.country === '_all_' || !apiData.country
        ? 'all'
        : (apiData.country_mode || apiData.countryMode || 'individual'),
    countries:
      Array.isArray(apiData.countries)
        ? apiData.countries
        : (typeof apiData.country === 'string' && apiData.country !== '_all_'
            ? apiData.country.split(',').map(s => s.trim()).filter(Boolean)
            : []),

    // Sector
    sectorMode:
      apiData.sector === '_all_' || !apiData.sector
        ? 'all'
        : (apiData.sector_mode || apiData.sectorMode || 'individual'),
    sectors:
      Array.isArray(apiData.sectors)
        ? apiData.sectors
        : (typeof apiData.sector === 'string' && apiData.sector !== '_all_'
            ? apiData.sector.split(',').map(s => s.trim()).filter(Boolean)
            : []),
    
    // Account selection
    accountSelectionMode: apiData.account_selection_mode || apiData.accountSelectionMode || 'all_in_sector',
    selectedAccountIds: apiData.selected_account_ids || apiData.selectedAccountIds || [],
    minAccountFitScore: apiData.min_account_fit_score ?? apiData.minAccountFitScore ?? undefined,

    // Persona audience mode fields
    personaAudienceMode: apiData.persona_audience_mode || apiData.personaAudienceMode || 'all_matching',
    selectedPersonaIds: (() => {
      const raw = apiData.selected_persona_ids ?? apiData.selectedPersonaIds;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    })(),
    messagingLanguageMode:
      apiData.messaging_language_mode ||
      apiData.messagingLanguageMode ||
      'auto_by_country',

    // Persona filtering
    includeAllArchetypes: apiData.include_all_archetypes ?? apiData.includeAllArchetypes ?? true,
    archetypes: apiData.archetypes || [],
    includeAllDepartments: apiData.include_all_departments ?? apiData.includeAllDepartments ?? true,
    departments: apiData.departments || [],
    roleTitleKeywords: apiData.role_title_keywords || apiData.roleTitleKeywords || [],

    // Product alignment (playbook-level)
    selectedProductKeys: Array.isArray(apiData.selected_product_keys)
      ? apiData.selected_product_keys
      : Array.isArray(apiData.selectedProductKeys)
        ? apiData.selectedProductKeys
        : [],

    // Signals
    useSignals: apiData.use_signals ?? apiData.useSignals ?? true,
    
    // Channels & steps (sanitized above when personalizationData is passed)
    enabledChannels: apiData.enabled_channels || apiData.enabledChannels || ['email'],
    steps,

    // Sending behaviour (prefer camel — GET returns both; same value)
    autoScheduleEmails: apiData.autoScheduleEmails ?? apiData.auto_schedule_emails ?? false,

    id: apiData.id || apiData.playbook_id || null,
    owner_user_id: diyPlaybookOwnerUserIdFromApi(apiData),
  };
};

/**
 * API persistence: some playbook fields expect snake_case on the wire while the form uses camelCase.
 * Merge these on save/run for persona targeting. (Do not add `auto_schedule_emails` here — BE reads
 * `autoScheduleEmails` from the spread `values` only; a snake alias would be ignored and misleading.)
 */
function diyPlaybookPersistenceAliases(values) {
  const mode = values?.personaAudienceMode ?? 'all_matching';
  const ids = Array.isArray(values?.selectedPersonaIds) ? values.selectedPersonaIds : [];
  const lang = values?.messagingLanguageMode;
  const messaging =
    lang === 'english_only' || lang === 'auto_by_country' ? lang : 'auto_by_country';
  const productKeys = Array.isArray(values?.selectedProductKeys)
    ? values.selectedProductKeys
    : [];
  return {
    persona_audience_mode: mode,
    selected_persona_ids: ids,
    messaging_language_mode: messaging,
    selected_product_keys: productKeys,
    goal_description: values?.goalDescription ?? '',
    enabled_channels: Array.isArray(values?.enabledChannels) ? values.enabledChannels : [],
    use_signals: values?.useSignals ?? true,
    selected_account_ids: Array.isArray(values?.selectedAccountIds)
      ? values.selectedAccountIds
      : [],
    account_selection_mode: values?.accountSelectionMode ?? 'all_in_sector',
    include_all_archetypes: values?.includeAllArchetypes ?? true,
    include_all_departments: values?.includeAllDepartments ?? true,
    role_title_keywords: Array.isArray(values?.roleTitleKeywords)
      ? values.roleTitleKeywords
      : [],
    min_account_fit_score:
      values?.accountSelectionMode === 'by_fit_score'
        ? (values?.minAccountFitScore ?? 0)
        : null,
  };
}

function diyCountrySectorWire(values) {
  const out = {};
  if (values?.countryMode === 'all') {
    out.country = '_all_';
    out.country_mode = 'all';
    out.countries = [];
  } else {
    const countries = Array.isArray(values?.countries) ? values.countries : [];
    out.country_mode = values?.countryMode || 'individual';
    out.countries = countries;
    out.country = countries.length > 0 ? countries.join(', ') : '_all_';
  }
  if (values?.sectorMode === 'all') {
    out.sector = '_all_';
    out.sector_mode = 'all';
    out.sectors = [];
  } else {
    const sectors = Array.isArray(values?.sectors) ? values.sectors : [];
    out.sector_mode = values?.sectorMode || 'individual';
    out.sectors = sectors;
    out.sector = sectors.length > 0 ? sectors.join(', ') : '_all_';
  }
  return out;
}

/** Steps: camelCase for FE + snake_case aliases many DIY save validators expect. */
function diyPlaybookStepsForApi(steps) {
  return (steps || []).map((step, idx) => {
    const order = step?.order ?? idx + 1;
    const channel = resolveDiyStepChannelFromApiStep(step);
    const row = {
      order,
      channel,
      day_offset: step?.dayOffset ?? 0,
      dayOffset: step?.dayOffset ?? 0,
      strategic_hook: step?.strategicHook ?? '',
      strategicHook: step?.strategicHook ?? '',
      message_focus: step?.messageFocus ?? '',
      messageFocus: step?.messageFocus ?? '',
      ...diyPlaybookLinkedInSubtypeWire(channel),
    };
    if (step?.id) row.id = step.id;
    const kpi = step?.kpiKey ?? null;
    if (kpi) {
      row.kpi_key = kpi;
      row.kpiKey = kpi;
    }
    const hook = step?.hookKey ?? null;
    if (hook) {
      row.hook_key = hook;
      row.hookKey = hook;
    }
    const cs = step?.caseStudyKey ?? null;
    if (cs) {
      row.case_study_key = cs;
      row.caseStudyKey = cs;
    }
    const pl = step?.productLinkKeys;
    if (Array.isArray(pl) && pl.length > 0) {
      row.product_link_keys = pl;
      row.productLinkKeys = pl;
    }
    const edu = step?.educationalResourceKeys;
    if (Array.isArray(edu) && edu.length > 0) {
      row.educational_resource_keys = edu;
      row.educationalResourceKeys = edu;
    }
    const cta = step?.ctaStyleKey ?? null;
    if (cta) {
      row.cta_style_key = cta;
      row.ctaStyleKey = cta;
    }
    return row;
  });
}

/** Omit FE-only meta; avoid duplicate id vs playbook_id confusing strict validators. */
function buildDiyPlaybookSavePayload(values, { tenantId, userId, playbookId }) {
  const { id: _feId, owner_user_id: _owner, ...formFields } = values || {};
  return {
    tenant_id: tenantId,
    ...(userId && { user_id: userId }),
    ...(playbookId && { playbook_id: playbookId }),
    ...formFields,
    steps: diyPlaybookStepsForApi(formFields.steps),
    ...diyCountrySectorWire(formFields),
    ...diyPlaybookPersistenceAliases(formFields),
  };
}

function formatDiySaveErrorDescription(errorData, status) {
  if (!errorData || typeof errorData !== 'object') {
    return `Server error: ${status}`;
  }
  const parts = [
    errorData.message,
    errorData.error,
    errorData.detail,
    typeof errorData.details === 'string' ? errorData.details : null,
    Array.isArray(errorData.details)
      ? errorData.details.map((d) => (typeof d === 'string' ? d : JSON.stringify(d))).join('; ')
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : `Server error: ${status}`;
}

/** DIY rows are often keyed by tenant + owner user_id; use this when saving edits as a different tenant member. */
function diyPlaybookOwnerUserIdFromApi(apiData) {
  if (!apiData || typeof apiData !== 'object') return null;
  const raw =
    apiData.user_id ?? apiData.userId ?? apiData.owner_user_id ?? apiData.created_by_user_id;
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  return s || null;
}

export default function DIYPlaybook({ currentTenant: currentTenantProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const contextUser = useUser();
  const currentTenantFromContext = useCurrentTenant();
  const currentTenant = currentTenantProp ?? currentTenantFromContext;
  const user = contextUser;

  /** Align with Playbooks list + Settings: use effective tenant (user.tenant_id, learnwise/evasys email rules, admin-active-tenant). */
  const resolveTenantId = () => {
    const adminTenant = user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    return String(
      resolveEffectiveTenant(adminTenant, currentTenant, user, user?.tenant_id || 'instructure')
    ).trim();
  };
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [playbookId, setPlaybookId] = useState(null);
  /** When editing someone else's shared playbook, saves must use the owner's user_id (API keys by tenant + user + playbook). */
  const [playbookOwnerUserId, setPlaybookOwnerUserId] = useState(null);
  const [blockedRetryItems, setBlockedRetryItems] = useState([]);
  const [blockedRetryErrors, setBlockedRetryErrors] = useState({});
  const [retryingStepIds, setRetryingStepIds] = useState([]);
  const [blockedRetryCount, setBlockedRetryCount] = useState(0);
  const blockedToastIdRef = useRef(null);
  const blockedToastFiredRef = useRef(false);
  const [inflightHint, setInflightHint] = useState(null);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (getActiveDiyGenerationPollCount() === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    if (isLoading || !contextUser) return;
    const tenantId = resolveTenantId();
    const uid = String(contextUser.user_id || contextUser.id || '').trim();
    if (!tenantId || !uid) return;
    const row = readDiyGenInflightLatest(tenantId, uid);
    if (!row?.playbookId) {
      setInflightHint(null);
      return;
    }
    const currentPid = playbookId ? String(playbookId).trim() : null;
    const storedPid = String(row.playbookId).trim();
    const sequenceIds = Array.isArray(row.sequenceIds) ? row.sequenceIds : [];
    if (currentPid && storedPid === currentPid) {
      setInflightHint({ playbookId: storedPid, sequenceIds, mismatched: false });
    } else {
      setInflightHint({ playbookId: storedPid, sequenceIds, mismatched: true });
    }
  }, [isLoading, contextUser, currentTenant, playbookId]);

  const getLocalPlaybooks = (tenantId) => {
    try {
      const stored = localStorage.getItem(`${LOCAL_STORAGE_KEY}-${tenantId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const saveLocalPlaybook = (tenantId, playbook) => {
    const existing = getLocalPlaybooks(tenantId);
    const idx = existing.findIndex(p => p.id === playbook.id);
    if (idx >= 0) {
      existing[idx] = playbook;
    } else {
      existing.push(playbook);
    }
    localStorage.setItem(`${LOCAL_STORAGE_KEY}-${tenantId}`, JSON.stringify(existing));
  };

  const deleteLocalPlaybook = (tenantId, id) => {
    const existing = getLocalPlaybooks(tenantId);
    const filtered = existing.filter(p => p.id !== id);
    localStorage.setItem(`${LOCAL_STORAGE_KEY}-${tenantId}`, JSON.stringify(filtered));
  };

  const formatBlockedStepLabel = (step) => {
    const name = step?.persona_name || 'Unknown persona';
    const touch = step?.step_number ? `Touch ${step.step_number}` : 'Touch ?';
    const channel = step?.channel || 'unknown channel';
    return `${name} (${touch}, ${channel})`;
  };

  const retryStep = async (stepId) => {
    if (!stepId || !contextUser?.id) return;
    const tenantId = resolveTenantId();
    setRetryingStepIds((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
    setBlockedRetryErrors((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
    try {
      const r = await fetch(`${API_BASE_URL}/api/playbooks/regenerate-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step_id: stepId, tenant_id: tenantId, user_id: contextUser.id }),
      });
      const data = await r.json().catch(() => ({}));
      if (data?.success) {
        setBlockedRetryItems((prev) => prev.filter((item) => item?.step_id !== stepId));
        setBlockedRetryErrors((prev) => {
          const next = { ...prev };
          delete next[stepId];
          return next;
        });
      } else {
        setBlockedRetryErrors((prev) => ({
          ...prev,
          [stepId]: data?.qa_issues?.[0] || data?.reason || 'Still failing',
        }));
      }
    } catch (error) {
      setBlockedRetryErrors((prev) => ({
        ...prev,
        [stepId]: error?.message || 'Still failing',
      }));
    } finally {
      setRetryingStepIds((prev) => prev.filter((id) => id !== stepId));
    }
  };

  useEffect(() => {
    if (!blockedRetryItems.length) {
      if (blockedToastIdRef.current) {
        toast.dismiss(blockedToastIdRef.current);
        blockedToastIdRef.current = null;
      }
      return;
    }

    if (blockedToastIdRef.current) {
      toast.dismiss(blockedToastIdRef.current);
    }

    blockedToastIdRef.current = toast.error(
      `⚠️  ${blockedRetryCount || blockedRetryItems.length} message(s) couldn't be generated. Retry below.`,
      {
        duration: Infinity,
        description: (
          <div className="space-y-2 mt-2">
            {blockedRetryItems.map((step) => {
              const stepId = step?.step_id;
              const retrying = retryingStepIds.includes(stepId);
              return (
                <div key={stepId || `${step?.person_id || 'unknown'}-${step?.step_number || 'na'}`} className="border border-red-200 rounded p-2 bg-red-50/60">
                  <div className="text-xs">{formatBlockedStepLabel(step)}</div>
                  {blockedRetryErrors[stepId] && (
                    <div className="text-[11px] text-red-700 mt-1">{blockedRetryErrors[stepId]}</div>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    className="mt-2 h-7 text-xs"
                    onClick={() => retryStep(stepId)}
                    disabled={!stepId || retrying}
                  >
                    {retrying ? 'Retrying...' : 'Retry'}
                  </Button>
                </div>
              );
            })}
          </div>
        ),
      }
    );
  }, [blockedRetryItems, blockedRetryErrors, retryingStepIds, blockedRetryCount]);

  useEffect(() => {
    const init = async () => {
      if (!contextUser) return;
      try {
        const tenantId = resolveTenantId();
        console.log('🚨 DIYPlaybook init tenant resolution:', {
          currentTenant,
          userTenant: contextUser?.tenant_id,
          resolvedTenant: tenantId
        });

        if (!tenantId) {
          throw new Error('Missing tenant_id for DIY Playbooks');
        }

        // Check if cloning from state
        if (location.state?.playbookData) {
          const sourceTenantId = location.state.playbookData?.tenant_id;

          if (sourceTenantId && !tenantIdsEqual(sourceTenantId, tenantId)) {
            console.warn('Blocked cross-tenant DIY playbook clone', { tenantId, sourceTenantId });
            toast.error('This playbook belongs to a different tenant');
            setIsLoading(false);
            return;
          }

          const resolvedUserId = String(contextUser?.user_id || contextUser?.id || '').trim();
          const personalizationCatalog = resolvedUserId
            ? await fetchPersonalizationCatalogForDiy(tenantId, resolvedUserId)
            : null;

          console.log('📋 Cloning playbook:', location.state.playbookData);
          const mapped = mapApiToForm(location.state.playbookData, personalizationCatalog);
          setPlaybookId(null);
          setPlaybookOwnerUserId(null);
          setInitialValues(mapped);
          setIsLoading(false);
          return;
        }

        // Check if editing existing playbook
        const params = new URLSearchParams(location.search);
        const editId = params.get('id');
        
        if (editId) {
          setPlaybookId(editId);

          const resolvedUserId = String(contextUser?.user_id || contextUser?.id || '').trim();
          const personalizationCatalog = resolvedUserId
            ? await fetchPersonalizationCatalogForDiy(tenantId, resolvedUserId)
            : null;

          // Try API first
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/diy-playbooks/${editId}?tenant_id=${tenantId}`,
              { credentials: 'include' }
            );
            
            if (response.ok) {
              const data = await response.json();
              const playbookData = data.playbook || data;
              console.log('📖 Loaded playbook from API:', playbookData);
              const mapped = mapApiToForm(playbookData, personalizationCatalog);
              setPlaybookOwnerUserId(diyPlaybookOwnerUserIdFromApi(playbookData));
              setInitialValues(mapped);
            } else {
              // Fallback to local storage
              const localPlaybooks = getLocalPlaybooks(tenantId);
              const found = localPlaybooks.find(p => p.id === editId);
              if (found) {
                const mapped = mapApiToForm(found, personalizationCatalog);
                setPlaybookOwnerUserId(diyPlaybookOwnerUserIdFromApi(found));
                setInitialValues(mapped);
              } else {
                setPlaybookOwnerUserId(null);
              }
            }
          } catch {
            // Fallback to local storage
            const localPlaybooks = getLocalPlaybooks(tenantId);
            const found = localPlaybooks.find(p => p.id === editId);
            if (found) {
              const mapped = mapApiToForm(found, personalizationCatalog);
              setPlaybookOwnerUserId(diyPlaybookOwnerUserIdFromApi(found));
              setInitialValues(mapped);
            } else {
              setPlaybookOwnerUserId(null);
            }
          }
        } else {
          setPlaybookId(null);
          setPlaybookOwnerUserId(null);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        if (error?.message?.includes('Missing tenant')) {
          toast.error('Missing tenant context');
          navigate(createPageUrl('Playbooks'));
        }
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [location.search, location.state, currentTenant, contextUser]);

  const handleSubmit = async (values, andRun = false) => {
    console.log('PAGE handleSubmit called', { andRun });
    if (!user) {
      toast.error('Please wait', { description: 'User session is still loading...' });
      return;
    }

    const tenantId = resolveTenantId();
    console.log('🚨 DIYPlaybook submit tenant resolution:', {
      currentTenant,
      userTenant: user?.tenant_id,
      resolvedTenant: tenantId
    });

    if (!tenantId) {
      toast.error('Missing tenant context');
      setIsSaving(false);
      return;
    }

    setIsSaving(true);

    const editorUserId = String(user.user_id || user.id || '').trim();
    const payloadUserId = editorUserId || undefined;

    try {
      let savedId = playbookId;
      let apiSuccess = false;

      console.assert(
        typeof values?.autoScheduleEmails === 'boolean',
        '[DIY] autoScheduleEmails must be boolean on save/run (BE coerces undefined to false)'
      );

      const savePayload = buildDiyPlaybookSavePayload(values, {
        tenantId,
        userId: payloadUserId,
        playbookId,
      });

      console.log('🚨 DIY save payload ids:', {
        tenantId,
        editorUserId,
        playbookOwnerUserId,
        payloadUserId,
        stepAssetKeys: (savePayload.steps || []).map((s) => ({
          order: s.order,
          kpi: s.kpi_key ?? s.kpiKey ?? null,
          hook: s.hook_key ?? s.hookKey ?? null,
        })),
      });

      const saveHeaders = { 'Content-Type': 'application/json' };
      if (payloadUserId) saveHeaders['X-User-ID'] = payloadUserId;
      if (tenantId) saveHeaders['X-Tenant-ID'] = tenantId;

      // Try API first
      try {
        const saveResponse = await fetch(`${API_BASE_URL}/api/diy-playbooks`, {
          method: 'POST',
          headers: saveHeaders,
          credentials: 'include',
          body: JSON.stringify(savePayload)
        });

        if (saveResponse.ok) {
          const result = await saveResponse.json();
          // Handle nested response structure: { success: true, playbook: {...} }
          const savedPlaybook = result.playbook || result;
          savedId = savedPlaybook.id || savedPlaybook.playbook_id || savedId;
          apiSuccess = true;
          console.log('✅ API save succeeded, playbook ID:', savedId);
        } else {
          const errorData = await saveResponse.json().catch(() => ({}));
          console.warn('[diy_playbook_save] failed', {
            status: saveResponse.status,
            errorData,
            payloadSummary: {
              playbook_id: savePayload.playbook_id,
              selected_product_keys: savePayload.selected_product_keys,
              steps: savePayload.steps,
            },
          });
          if (saveResponse.status === 403 && errorData?.error === 'ownership_required') {
            setIsSaving(false);
            return { ok: false, status: 403, errorData };
          }
          toast.error('Failed to save playbook', {
            description: formatDiySaveErrorDescription(errorData, saveResponse.status),
          });
          setIsSaving(false);
          return { ok: false, status: saveResponse.status, errorData };
        }
      } catch (apiError) {
        console.warn('API save failed:', apiError);
        toast.error('Failed to save playbook', {
          description: 'Network error. Saved locally only.'
        });
        // Fall through to save locally
      }

      // Save to local storage as backup (use API ID if available)
      const localPlaybook = {
        id: savedId || playbookId || crypto.randomUUID(),
        tenant_id: tenantId,
        user_id: payloadUserId || editorUserId,
        created_at: new Date().toISOString(),
        ...values,
        ...diyPlaybookPersistenceAliases(values),
      };
      saveLocalPlaybook(tenantId, localPlaybook);
      
      // Only update savedId if we didn't get one from API
      if (!apiSuccess) {
        savedId = localPlaybook.id;
      }

      if (!andRun) {
        toast.success('Playbook saved!', {
          description: apiSuccess ? `"${values.name}" has been saved.` : `"${values.name}" saved locally (API unavailable).`
        });
      }

      console.log('PAGE save completed', { savedId, apiSuccess, andRun });

      if (andRun) {
        try {
          const inflightExisting = readDiyGenInflightLatest(tenantId, editorUserId);
          const targetPlaybookId = String(savedId || playbookId || '').trim();
          if (
            inflightExisting &&
            targetPlaybookId &&
            String(inflightExisting.playbookId) === targetPlaybookId
          ) {
            toast.error('Generation already in progress', {
              description:
                'Open Outbox or wait for the current run to finish before running again.',
            });
            return { ok: true };
          }

          const runPayload = buildDiyPlaybookSavePayload(values, {
            tenantId,
            userId: payloadUserId,
            playbookId: savedId,
          });
          const runHeaders = { 'Content-Type': 'application/json' };
          if (payloadUserId) runHeaders['X-User-ID'] = payloadUserId;
          if (tenantId) runHeaders['X-Tenant-ID'] = tenantId;
          const runResponse = await fetch(`${API_BASE_URL}/api/diy-playbooks/run`, {
            method: 'POST',
            headers: runHeaders,
            credentials: 'include',
            body: JSON.stringify(runPayload)
          });
          const runResult = await runResponse.json().catch(() => ({}));

          if (!runResponse.ok || runResult.success === false) {
            toast.error(runResult?.error || 'Failed to run playbook');
            return { ok: true };
          }

          if (runResult.generation_status === 'failed') {
            toast.error(runResult?.error || 'Generation failed');
            return { ok: true };
          }

          const runData =
            runResult?.data && typeof runResult.data === 'object'
              ? { ...runResult, ...runResult.data }
              : runResult;
          const sequenceIds = normalizeRunSequenceIds(runData);
          const expectedSeconds = Number(runData?.expected_seconds) || 300;
          const startedAt = runData?.started_at || null;
          const genStatus = runData.generation_status;

          // pending means drafts are not ready yet — never claim "messages generated"
          // or redirect from the run payload. Poll status until every sequence is complete.
          if (sequenceIds.length > 0 && genStatus !== 'failed') {
            const inflightExpiresAt = Date.now() + diyGenerationSafetyCapMs(expectedSeconds) + 120000;
            writeDiyGenInflightLatest(tenantId, editorUserId, {
              playbookId: String(savedId),
              sequenceIds,
              expiresAt: inflightExpiresAt,
            });
            setInflightHint({
              playbookId: String(savedId),
              sequenceIds,
              mismatched: false,
            });

            void runDiyPlaybookGenerationPoll({
              tenantId,
              userId: payloadUserId || editorUserId,
              sequenceIds,
              expectedSeconds,
              startedAt,
              stepsCreated: runData?.steps_created ?? runData?.total_steps,
              navigate,
              analytics: {
                tenantId,
                userId: payloadUserId || editorUserId,
                playbookId: String(savedId),
                accountId: values.selectedAccountIds?.[0] || null,
                personId: values.selectedPersonaIds?.[0] || null,
                metadata: { playbook_name: values.name },
              },
              onSettled: (pollResult) => {
                if (
                  pollResult.completedInPoll ||
                  pollResult.timedOut ||
                  pollResult.ok === false
                ) {
                  clearDiyGenInflightLatest(tenantId, editorUserId);
                  setInflightHint(null);
                }
              },
            });
            return { ok: true };
          }

          toast.error('Run started but no sequences were returned', {
            description:
              'The server accepted the run without sequence ids to poll. Check Outbox or try again.',
          });
          return { ok: true };
        } catch (runError) {
          console.error('DIY run error', runError);
          if (runError?.name === 'AbortError') return { ok: true };
          toast.error('Failed to run playbook', {
            description: runError?.message || String(runError),
          });
          return { ok: true };
        }
      } else {
        navigate(createPageUrl('Playbooks'));
        return { ok: true };
      }
    } catch (error) {
      console.error('Error submitting playbook:', error);
      toast.error('Error', {
        description: error.message || 'Failed to save playbook'
      });
      return { ok: false };
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!playbookId || !user) return;

    const tenantId = resolveTenantId();
    console.log('🚨 DIYPlaybook delete tenant resolution:', {
      currentTenant,
      userTenant: user?.tenant_id,
      resolvedTenant: tenantId
    });

    if (!tenantId) {
      toast.error('Missing tenant context');
      setIsDeleting(false);
      return;
    }

    setIsDeleting(true);

    try {
      // Try API first
      try {
        const delQs = new URLSearchParams({ tenant_id: tenantId });
        if (playbookOwnerUserId) delQs.set('user_id', playbookOwnerUserId);
        await fetch(
          `${API_BASE_URL}/api/diy-playbooks/${playbookId}?${delQs.toString()}`,
          {
            method: 'DELETE',
            credentials: 'include'
          }
        );
      } catch {
        // Ignore API errors
      }

      // Always delete from local storage
      deleteLocalPlaybook(tenantId, playbookId);

      toast.success('Playbook deleted');
      navigate(createPageUrl('Playbooks'));
    } catch (error) {
      console.error('Error deleting playbook:', error);
      toast.error('Failed to delete playbook');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCancel = () => {
    navigate(createPageUrl('Playbooks'));
  };

  const handleDismissInflightHint = () => {
    const tenantId = resolveTenantId();
    const uid = String(user?.user_id || user?.id || '').trim();
    clearDiyGenInflightLatest(tenantId, uid);
    setInflightHint(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  if (!resolveTenantId()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  console.log('🚨 DIYPlaybook render tenant resolution:', {
    currentTenant,
    userTenant: user?.tenant_id,
    resolvedTenant: resolveTenantId()
  });

  const editorUserId = String(user?.user_id || user?.id || '').trim();
  const isOwnedPlaybook =
    playbookId &&
    (!playbookOwnerUserId || !editorUserId || String(playbookOwnerUserId) === editorUserId);

  const handleClonePlaybook = async () => {
    if (!playbookId) return;
    const tenantId = resolveTenantId();
    if (!tenantId || !editorUserId) {
      toast.error('Missing tenant or user context');
      return;
    }
    setIsCloning(true);
    try {
      const result = await cloneDiyPlaybook({
        playbookId,
        tenantId,
        userId: editorUserId,
        cloneSource: 'diy_playbook_header',
        playbookName: initialValues?.name ?? null,
        sourceOwnerUserId: playbookOwnerUserId,
      });
      if (!result.ok) {
        if (result.reason === 'missing_new_id') {
          toast.error('Clone succeeded but no playbook id was returned.');
        } else {
          toast.error('Failed to clone playbook. Try again.');
        }
        return;
      }
      navigate(`${createPageUrl('DIYPlaybook')}?id=${encodeURIComponent(result.newPlaybookId)}`);
      toast.success('Playbook cloned. Opening copy…');
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(createPageUrl('Playbooks'))}
          className="-ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Playbooks
        </Button>
      </div>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1A2F]">
            {playbookId ? 'Edit Playbook' : 'Create Custom Playbook'}
          </h1>
          <p className="text-[#5C6B7A] mt-1">
            Define your targeting, sequence, and messaging strategy
          </p>
        </div>
        {playbookId && (
          <div className="flex flex-wrap gap-2">
            {isOwnedPlaybook && (
              <Button
                variant="outline"
                onClick={handleClonePlaybook}
                disabled={isCloning || isSaving}
                title="Duplicate this playbook including all step assets"
              >
                {isCloning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                Clone
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isCloning}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {inflightHint && (
        <div
          role="status"
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
        >
          <p className="font-medium">
            {inflightHint.mismatched
              ? 'A playbook run may still be completing in this workspace.'
              : 'Generation may still be in progress for this playbook.'}
          </p>
          <p className="mt-1 text-amber-900/90">
            Check Outbox before running again to avoid duplicate sequences.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => navigate(createPageUrl('Outbox'))}>
              Open Outbox
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-amber-300 bg-white text-amber-950 hover:bg-amber-100"
              onClick={handleDismissInflightHint}
            >
              I have checked Outbox — dismiss
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Playbook</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this playbook? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DIYPlaybookForm
        initialValues={initialValues}
        tenantId={resolveTenantId()}
        userId={String(user?.id || user?.user_id || '').trim() || undefined}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSaving={isSaving}
      />
    </div>
  );
}